import { useEffect, useRef, useState } from 'react';

interface DemoVideoProps {
  /** /videos/web/ 配下のファイル名（PC用・16:10素材。例: "demo_ai.mp4"） */
  src: string;
  /** ポスター画像のファイル名（例: "poster_ai.jpg"） */
  poster: string;
  /** モバイル用のファイル名（4:5・スマホ実画面素材。例: "demo_ai_m.mp4"）。
      指定すると sm 未満ではこちらを表示する */
  mobileSrc?: string;
  mobilePoster?: string;
  /** アクセシビリティ用の説明 */
  label: string;
  /** PC側のアスペクト比クラス */
  aspectClassName?: string;
  /** モバイル側のアスペクト比クラス（mobileSrc 指定時） */
  mobileAspectClassName?: string;
  className?: string;
}

/**
 * ビューポート内でだけ再生する単一の動画要素。
 * prefers-reduced-motion 環境では再生せずポスターを表示。
 */
function VideoEl({
  src,
  poster,
  label,
  className,
}: {
  src: string;
  poster: string;
  label: string;
  className: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 冒頭はループ継ぎ目用の背景色フェードなので、初回だけスキップして
            // 「白い一瞬」を見せない（2周目以降の継ぎ目はフェードとして機能する）
            if (video.currentTime === 0) {
              video.currentTime = 0.55;
            }
            // 自動再生がブロックされた場合はポスター表示のまま（握りつぶしてOK）
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [reducedMotion]);

  return (
    <video
      ref={videoRef}
      className={`w-full h-full object-cover ${className}`}
      src={`/videos/web/${src}`}
      poster={`/videos/web/${poster}`}
      loop
      muted
      playsInline
      preload="metadata"
      aria-label={label}
    />
  );
}

/**
 * Webサイト埋め込み用のデモ動画（無音・ループ）。
 *
 * PC は 16:10 のPC実画面、モバイルは 4:5 のスマホ実画面と、
 * 閲覧デバイスに合わせた素材を出し分ける（mobileSrc 指定時）。
 * 動画は remotion-video の WebEmbeds シリーズ（render-web.sh で生成）。
 */
export default function DemoVideo({
  src,
  poster,
  mobileSrc,
  mobilePoster,
  label,
  aspectClassName = 'aspect-[16/10]',
  mobileAspectClassName = 'aspect-[4/5]',
  className = '',
}: DemoVideoProps) {
  if (!mobileSrc) {
    return (
      <div className={`overflow-hidden bg-[#fafaf8] ${aspectClassName} ${className}`}>
        <VideoEl src={src} poster={poster} label={label} className="" />
      </div>
    );
  }
  return (
    <>
      {/* モバイル: スマホ実画面（4:5） */}
      <div className={`sm:hidden overflow-hidden bg-[#fafaf8] ${mobileAspectClassName} ${className}`}>
        <VideoEl src={mobileSrc} poster={mobilePoster ?? poster} label={label} className="" />
      </div>
      {/* PC: PC実画面（16:10） */}
      <div className={`hidden sm:block overflow-hidden bg-[#fafaf8] ${aspectClassName} ${className}`}>
        <VideoEl src={src} poster={poster} label={label} className="" />
      </div>
    </>
  );
}
